import { useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const LANGUAGES = [
	{ key: "en-us", label: "English (US)" },
	{ key: "de-ch", label: "Deutsch (CH)" },
	{ key: "de-de", label: "Deutsch (DE)" },
	{ key: "fr-fr", label: "Français" },
	{ key: "it-it", label: "Italiano" },
] as const;

const schema = z.object({
	"en-us": z.string().min(1, "English is required"),
	"de-ch": z.string().default(""),
	"de-de": z.string().default(""),
	"fr-fr": z.string().default(""),
	"it-it": z.string().default(""),
});

export type CategoryFormData = z.infer<typeof schema>;

interface Props {
	show: boolean;
	onHide: () => void;
	onSubmit: (data: CategoryFormData) => void;
	title: string;
	defaultValues?: CategoryFormData;
}

export default function CategoryFormModal({ show, onHide, onSubmit, title, defaultValues }: Props) {
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CategoryFormData>({
		resolver: zodResolver(schema) as any,
		defaultValues: defaultValues ?? {
			"en-us": "",
			"de-ch": "",
			"de-de": "",
			"fr-fr": "",
			"it-it": "",
		},
	});

	useEffect(() => {
		if (show) {
			reset(
				defaultValues ?? {
					"en-us": "",
					"de-ch": "",
					"de-de": "",
					"fr-fr": "",
					"it-it": "",
				},
			);
		}
	}, [show, defaultValues, reset]);

	const handleFormSubmit = (data: CategoryFormData) => {
		onSubmit(data);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header closeButton>
				<Modal.Title className="text-truncate" style={{ maxWidth: "90%" }}>
					{title}
				</Modal.Title>
			</Modal.Header>
			<form onSubmit={handleSubmit(handleFormSubmit)}>
				<Modal.Body>
					{LANGUAGES.map((lang) => (
						<Form.Group className="mb-3" key={lang.key}>
							<Form.Label>
								{lang.label}
								{lang.key === "en-us" && <span className="text-danger ms-1">*</span>}
							</Form.Label>
							<Form.Control
								type="text"
								placeholder={lang.label}
								isInvalid={!!errors[lang.key]}
								{...register(lang.key)}
							/>
							{errors[lang.key] && (
								<Form.Control.Feedback type="invalid">
									{errors[lang.key]?.message}
								</Form.Control.Feedback>
							)}
						</Form.Group>
					))}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="outline-secondary" onClick={onHide}>
						Cancel
					</Button>
					<Button variant="primary" type="submit">
						Save
					</Button>
				</Modal.Footer>
			</form>
		</Modal>
	);
}
